import { useCallback, useState } from 'react';
import type { SeatGeometry } from './types';

/**
 * Selection state shared by the canvas and the list: click selects, shift-click
 * or ctrl/cmd-click toggles, a marquee replaces or (with shift) adds.
 */
export function useSeatSelection() {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());

  const clear = useCallback(() => setSelected(new Set()), []);

  const clickSeat = useCallback((seat: SeatGeometry, modifiers: { shiftKey: boolean; metaKey: boolean }) => {
    setSelected((current) => {
      if (modifiers.shiftKey || modifiers.metaKey) {
        const next = new Set(current);
        if (next.has(seat.id)) next.delete(seat.id);
        else next.add(seat.id);
        return next;
      }
      if (current.size === 1 && current.has(seat.id)) return new Set();
      return new Set([seat.id]);
    });
  }, []);

  const toggleSeat = useCallback((seat: SeatGeometry) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(seat.id)) next.delete(seat.id);
      else next.add(seat.id);
      return next;
    });
  }, []);

  const marquee = useCallback((ids: string[], additive: boolean) => {
    setSelected((current) => (additive ? new Set([...current, ...ids]) : new Set(ids)));
  }, []);

  const selectMany = useCallback((ids: Iterable<string>) => setSelected(new Set(ids)), []);

  return { selected, clear, clickSeat, toggleSeat, marquee, selectMany };
}
